import { BILLING_LABEL, billingLabelFor, REV_CREDIT } from "./studioConstants";

export const emptyForm = () => ({
  name: "", type: "plan", status: "active",
  billing: { period: "yearly", label: BILLING_LABEL.yearly },
  planTitle: "", planSubtitle: "",
  features: [],
  fulfillment: null,
  audience: { mode: "everyone", grants: [] },
  // The plan bills as one flat amount; add-ons bill per unit, keyed by catalog
  // key (two flow packs are two keys, so a per-family map can't hold both).
  subAmounts: { plan: 0 },
  subUnitPrices: {},
  creditPrices: { ctwaAmount: "", wccAmount: "" },
  originals: {},
  minPrice: "", currency: "INR", migrationDeadline: "",
});

// Old products carry { planName, flowAddOn, virtualNumberAddOn }; new ones carry
// { duration, planKey, addOns }. Virtual-number is dropped.
export const migrateFulfillment = (ff, billing) => {
  if (!ff) return null;
  const s = ff.subscription;
  if (!s) return ff;
  if ("duration" in s) return ff; // already new shape
  const duration = ["monthly", "quarterly", "yearly"].includes(billing?.period) ? billing.period : "monthly";
  const addOns = [];
  if (s.flowAddOn) addOns.push({ family: "flow_addon", key: s.flowAddOn, quantity: 1 });
  return { ...ff, subscription: { duration, planKey: s.planName || null, addOns } };
};

// Map an existing product (plus its active pricing version) into the form shape,
// so edit mode reuses the create UI with everything prefilled.
export const formFromProduct = (product) => {
  const cur = (product.pricingHistory || []).find((v) => v.effectiveTo === null)
    || (product.pricingHistory || [])[0] || null;
  const subAmounts = { plan: 0 };
  const subUnitPrices = {};
  const creditPrices = { ctwaAmount: "", wccAmount: "" };
  const originals = {};
  // Add-on rows are keyed by catalog key, so the blueprint has to be read first
  // to know which line-item ids are add-ons.
  const migrated = migrateFulfillment(product.fulfillment, product.billing);
  const addOnKeys = new Set(((migrated && migrated.subscription && migrated.subscription.addOns) || []).map((a) => a.key));
  (cur?.lineItems || []).forEach((l) => {
    if (l.originalAmount != null) originals[l.id] = l.originalAmount;
    if (l.id === "plan") subAmounts.plan = l.amount;
    else if (REV_CREDIT[l.id]) creditPrices[REV_CREDIT[l.id]] = l.unitPrice;
    else if (addOnKeys.has(l.id)) subUnitPrices[l.id] = l.unitPrice;
  });
  return {
    name: product.name || "", type: product.type || "plan", status: product.status || "active",
    billing: (() => {
      const period = product.billing?.period || "yearly";
      return { period, label: billingLabelFor(period) };
    })(),
    planTitle: product.planTitle || "", planSubtitle: product.planSubtitle || "",
    features: product.features || [],
    fulfillment: migrated,
    audience:
      product.audience && product.audience.mode
        ? { mode: product.audience.mode, grants: product.audience.grants || [] }
        : { mode: "everyone", grants: [] },
    subAmounts, creditPrices, subUnitPrices, originals,
    minPrice: cur?.minPrice ?? "", currency: cur?.currency || "INR", migrationDeadline: "",
  };
};

export const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

// Canonical (fixed key order, coerced) fulfillment for equality checks.
const canonFulfillment = (ff) => {
  if (!ff) return null;
  const out = {};
  if (ff.subscription) out.subscription = {
    duration: ff.subscription.duration || null,
    planKey: ff.subscription.planKey || null,
    addOns: (ff.subscription.addOns || []).map((a) => ({ family: a.family, key: a.key, quantity: Number(a.quantity) || 1 })),
  };
  if (ff.credits) out.credits = {
    ctwaAmount: Number(ff.credits.ctwaAmount) || 0,
    wccAmount: Number(ff.credits.wccAmount) || 0,
  };
  if (ff.webinar) out.webinar = {
    meetingLink: ff.webinar.meetingLink || "",
    emailSubject: ff.webinar.emailSubject || "",
    whatsappTemplateName: ff.webinar.whatsappTemplateName || "",
  };
  return out;
};

// Canonical audience string for dirty-checking: order-independent over grants.
const canonAudience = (a) => {
  if (!a || a.mode !== "restricted") return "everyone";
  const ids = (a.grants || []).map((g) => `${g.audienceType}:${g.audienceId}`).sort();
  return `restricted:${ids.join(",")}`;
};

// Snapshot of what "Save changes" sends, for dirty-checking. Excludes pricing (a
// separate versioned action) and type (locked in edit).
export const editSnapshot = (src) => JSON.stringify({
  name: (src.name || "").trim(),
  status: src.status || "active",
  period: src.billing?.period || "",
  label: (src.billing?.label || "").trim(),
  planTitle: (src.planTitle || "").trim(),
  planSubtitle: (src.planSubtitle || "").trim(),
  features: src.features || [],
  fulfillment: canonFulfillment(src.fulfillment),
  audience: canonAudience(src.audience),
});
